import type { ContactFieldValueInput } from '../address-book/types';
import type { UploadRequestResponse } from '../files/types';

// ==================== Enums ====================

export const ContactProvider = {
  Google: 'Google',
  Microsoft365: 'Microsoft365',
  VCard: 'VCard',
  Csv: 'Csv',
  Phone: 'Phone',
  BusinessCard: 'BusinessCard',
} as const;

export type ContactProviderValue = (typeof ContactProvider)[keyof typeof ContactProvider];

export const ImportSourceKind = {
  OAuth: 'OAuth',
  File: 'File',
} as const;

export type ImportSourceKindValue = (typeof ImportSourceKind)[keyof typeof ImportSourceKind];

export const ImportSelectionMode = {
  All: 'All',
  Selected: 'Selected',
} as const;

export type ImportSelectionModeValue =
  (typeof ImportSelectionMode)[keyof typeof ImportSelectionMode];

/** Session lifecycle: `Building` (preview) → `Draft` (resolving) → `Committing` (writing) →
 * `Committed`. `Stopped`/`Expired` are the two ways it ends without completing. */
export const ImportJobStatus = {
  Queued: 'Queued',
  Running: 'Running',
  Completed: 'Completed',
  PartiallyCompleted: 'PartiallyCompleted',
  Failed: 'Failed',
  Building: 'Building',
  Draft: 'Draft',
  Committing: 'Committing',
  Committed: 'Committed',
  Stopped: 'Stopped',
  Expired: 'Expired',
} as const;

export type ImportJobStatusValue = (typeof ImportJobStatus)[keyof typeof ImportJobStatus];

/** Resolution bucket assigned to every previewed import row. */
export const ImportRowBucket = {
  New: 'New',
  AutoMerge: 'AutoMerge',
  NeedsReview: 'NeedsReview',
  Skipped: 'Skipped',
} as const;

export type ImportRowBucketValue = (typeof ImportRowBucket)[keyof typeof ImportRowBucket];

/** Why a row was bucketed `Skipped`. */
export const ImportSkipReason = {
  NoIdentity: 'NoIdentity',
  DuplicateInFile: 'DuplicateInFile',
  Unreadable: 'Unreadable',
} as const;

export type ImportSkipReasonValue = (typeof ImportSkipReason)[keyof typeof ImportSkipReason];

/** Why a row was bucketed `NeedsReview`. */
export const ImportReviewReason = {
  NameMismatch: 'NameMismatch',
  MultipleCandidates: 'MultipleCandidates',
  EmailPhoneConflict: 'EmailPhoneConflict',
  WeakMatchOnly: 'WeakMatchOnly',
  FullNameRequired: 'FullNameRequired',
} as const;

export type ImportReviewReasonValue = (typeof ImportReviewReason)[keyof typeof ImportReviewReason];

/** The user's resolution decision for a previewed row, overriding the bucket default. */
export const ImportRowDecision = {
  CreateNew: 'CreateNew',
  MergeInto: 'MergeInto',
  Skip: 'Skip',
} as const;

export type ImportRowDecisionValue = (typeof ImportRowDecision)[keyof typeof ImportRowDecision];

export const ImportItemStatus = {
  Pending: 'Pending',
  Done: 'Done',
  Failed: 'Failed',
  Skipped: 'Skipped',
} as const;

export type ImportItemStatusValue = (typeof ImportItemStatus)[keyof typeof ImportItemStatus];

export const ImportRowFailureCode = {
  EmailAlreadyExists: 'EmailAlreadyExists',
  MissingFullName: 'MissingFullName',
  MissingPayload: 'MissingPayload',
  SkipEffectiveButQueued: 'SkipEffectiveButQueued',
  TargetOwnerMismatch: 'TargetOwnerMismatch',
  Unexpected: 'Unexpected',
} as const;

export type ImportRowFailureCodeValue =
  (typeof ImportRowFailureCode)[keyof typeof ImportRowFailureCode];

/** Per-field agreement verdict between an import row and a candidate contact. */
export const FieldVerdict = {
  Matches: 'Matches',
  Contradicts: 'Contradicts',
  Neutral: 'Neutral',
} as const;

export type FieldVerdictValue = (typeof FieldVerdict)[keyof typeof FieldVerdict];

/** The fields compared field-by-field between an import row and a candidate contact. */
export const ComparedField = {
  Name: 'Name',
  Email: 'Email',
  Phone: 'Phone',
  Company: 'Company',
  JobTitle: 'JobTitle',
  Website: 'Website',
  Address: 'Address',
} as const;

export type ComparedFieldValue = (typeof ComparedField)[keyof typeof ComparedField];

export const NameConsistency = {
  Consistent: 'Consistent',
  Substantive: 'Substantive',
  NoName: 'NoName',
} as const;

export type NameConsistencyValue = (typeof NameConsistency)[keyof typeof NameConsistency];

/** The two bulk helpers over the "unresolved" set (`bucket = NeedsReview AND decision IS NULL`). */
export const SessionBulkAction = {
  KeepRemainingAsNew: 'KeepRemainingAsNew',
  SkipRemaining: 'SkipRemaining',
} as const;

export type SessionBulkActionValue = (typeof SessionBulkAction)[keyof typeof SessionBulkAction];

/** Per-row outcome codes for a failed `PATCH /sessions/:id/rows` patch. Always returned in-body. */
export const RowPatchConflictCode = {
  NotFound: 'NotFound',
  Conflict: 'Conflict',
  InvalidTarget: 'InvalidTarget',
  /** `decision = CreateNew` on a row whose email already belongs to an existing contact. */
  EmailAlreadyExists: 'EmailAlreadyExists',
  FullNameRequired: 'FullNameRequired',
  FullNameNotEditable: 'FullNameNotEditable',
} as const;

export type RowPatchConflictCodeValue =
  (typeof RowPatchConflictCode)[keyof typeof RowPatchConflictCode];

// ==================== Shared shapes ====================

/** One labelled value (email/phone), for a row's payload subset. */
export interface ProviderValue {
  value: string;
  label: string | null;
}

/** One field an AutoMerge preview would add to the matched contact (additive-merge semantics). */
export interface AddedField {
  /** 'Email' | 'Phone' | 'JobTitle' | 'Company' | 'Website' | 'Social' | 'Address' | 'Note' */
  field: string;
  value: string;
}

/** Per-field agreement between an import row and one ranked candidate. */
export interface CandidateFieldVerdict {
  field: ComparedFieldValue;
  value: string | null;
  verdict: FieldVerdictValue;
}

/** A NeedsReview candidate the row could merge into, with its match score and per-field verdicts. */
export interface RankedCandidate {
  contactId: string;
  score: number;
  matchedFields: ComparedFieldValue[];
  fieldVerdicts: CandidateFieldVerdict[];
  nameConsistency: NameConsistencyValue;
  /**
   * The row's own imported Email/Phone/JobTitle/Company values this candidate ALREADY holds. Absent
   * on candidates classified before the field existed — treat that as "hide nothing".
   */
  alreadyPresentFields?: AddedField[];
}

export interface EffectiveDecision {
  decision: ImportRowDecisionValue;
  targetContactId: string | null;
}

export interface ImportBuildCounts {
  new: number;
  autoMerge: number;
  needsReview: number;
  skipped: number;
}

export interface SessionFooterCounts {
  create: number;
  merge: number;
  skip: number;
  /** Unresolved NeedsReview rows still in the commit set ("N need review"). */
  needsReviewUnresolved: number;
  total: number;
}

export interface BuildProgress {
  /** Rows classified so far. */
  processed: number;
  /** Light progress hint; null when indeterminate. */
  totalEstimate: number | null;
}

export interface CommitProgress {
  /** Rows with committedAt set. */
  written: number;
  /** Rows in the commit set (willCommit = true). */
  total: number;
}

/** Realized commit-phase counters, folded from `job.counters`; zeros before any commit page has run. */
export interface SessionCommitCounts {
  created: number;
  merged: number;
  failed: number;
  skipped: number;
}

// ==================== Selection ====================

export interface ImportSelection {
  mode: ImportSelectionModeValue;
  /** Provider externalIds (required for mode = Selected). */
  externalIds?: string[];
}

// ==================== Uploads (POST /contact-import/uploads) ====================

export interface CreateContactImportUploadPayload {
  filename: string;
  contentType: string;
  size: number;
}

export type CreateContactImportUploadRes = UploadRequestResponse;

// ==================== Sessions: create (POST /contact-import/sessions) ====================

export interface CreateImportSessionPayload {
  provider: ContactProviderValue;
  sourceKind: ImportSourceKindValue;
  /** file-service file id (required for File sources). */
  fileId?: string;
  selection: ImportSelection;
}

export interface CreateImportSessionRes {
  /** Id of the created resolution session (Building until the preview is built). */
  sessionId: string;
}

// ==================== Sessions: list (GET /contact-import/sessions) ====================

/** `GET /contact-import/sessions` list item — cheap, no per-row queries. */
export interface SessionListItem {
  id: string;
  provider: ContactProviderValue;
  sourceKind: ImportSourceKindValue;
  status: ImportJobStatusValue;
  createdAt: string;
  expiresAt: string | null;
  totalEstimate: number | null;
  buildCounts: ImportBuildCounts | null;
}

// ==================== Sessions: summary (GET /contact-import/sessions/:id) ====================

/** `GET /contact-import/sessions/:id` — status, build/commit progress, bucket counts, footer. */
export interface SessionSummary {
  status: ImportJobStatusValue;
  provider: ContactProviderValue;
  createdAt: string;
  expiresAt: string | null;
  /** Present only while Building. */
  buildProgress: BuildProgress | null;
  /** Present once the build finished. */
  buildCounts: ImportBuildCounts | null;
  commitProgress: CommitProgress;
  /** Committed rows where a stale ranked candidate forced a fallback decision. */
  staleFallbackCount: number;
  /** Fresh, never cached. */
  footer: SessionFooterCounts;
  /** Realized commit-phase counters, folded from job.counters; zeros before any commit page has run. */
  counters: SessionCommitCounts;
  errorSummary: string | null;
}

// ==================== Sessions: rows (GET /contact-import/sessions/:id/rows) ====================

export const SESSION_ROWS_DEFAULT_PAGE_SIZE = 50;
export const SESSION_ROWS_MAX_PAGE_SIZE = 200;
/** `GET /contact-import/sessions` cap ("recent sessions", no paging params). */
export const RECENT_SESSIONS_LIMIT = 20;

export interface ListSessionRowsParams {
  /** Omitted = all buckets. */
  bucket?: ImportRowBucketValue;
  /** Case-insensitive substring match on displayLabel or any payload email. */
  search?: string;
  status?: ImportItemStatusValue;
  /** Keyset cursor: rows with position > afterPosition. */
  afterPosition?: number;
  pageSize?: number;
}

export interface SessionRowsFeedFilters {
  bucket?: ImportRowBucketValue;
  search?: string;
  status?: ImportItemStatusValue;
}

/** One structured postal address of a row's imported payload (parts kept separate, never pre-joined). */
export interface SessionRowAddress {
  street: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
  label: string | null;
}

/** The row's IMPORTED payload — the full provider-contact surface (GP-3877). */
export interface SessionRowPayload {
  fullName: string | null;
  emails: ProviderValue[];
  phones: ProviderValue[];
  company: string | null;
  jobTitle: string | null;
  addresses: SessionRowAddress[];
  websites: ProviderValue[];
  socialLinks: ProviderValue[];
  notes: string | null;
}

/** One resolution row. `effectiveDecision` mirrors the footer counts' pill logic — never re-derive it. */
export interface SessionRow {
  rowId: string;
  position: number;
  externalId: string | null;
  displayLabel: string | null;
  payload: SessionRowPayload | null;
  bucket: ImportRowBucketValue | null;
  skipReason: ImportSkipReasonValue | null;
  reviewReason: ImportReviewReasonValue | null;
  autoMergeContactId: string | null;
  addedFields: AddedField[] | null;
  candidates: RankedCandidate[] | null;
  decision: ImportRowDecisionValue | null;
  chosenContactId: string | null;
  checked: boolean;
  effectiveDecision: EffectiveDecision | null;
  committedAt: string | null;
  staleFallback: boolean;
  /** Optimistic-concurrency token for row-patch mutations. */
  updatedAt: string | null;
  status: ImportItemStatusValue;
  failureCode: ImportRowFailureCodeValue | null;
}

export interface SessionRowsPage {
  rows: SessionRow[];
  /** Pass as afterPosition to fetch the next page. */
  nextAfterPosition: number | null;
  hasMore: boolean;
}

// ==================== Sessions: row patches (PATCH /contact-import/sessions/:id/rows) ====================

/** One row patch. Every field but `rowId` is optional. */
export interface RowPatch {
  rowId: string;
  decision?: ImportRowDecisionValue | null;
  /** Required (and only meaningful) when decision = MergeInto. */
  chosenContactId?: string;
  checked?: boolean;
  fullName?: string | null;
  /** Opt-in optimistic-concurrency token — the row updatedAt value last read by the caller. */
  expectedUpdatedAt?: string;
}

export interface PatchSessionRowsPayload {
  patches: RowPatch[];
}

export interface RowPatchConflict {
  rowId: string;
  code: RowPatchConflictCodeValue;
  /** Present only for `Conflict` — the row's current server state, so the FE can re-render it. */
  row?: SessionRow;
}

export interface PatchSessionRowsRes {
  applied: string[];
  conflicts: RowPatchConflict[];
  footer: SessionFooterCounts;
}

// ==================== Sessions: bulk action (POST /contact-import/sessions/:id/bulk) ====================

export interface BulkSessionActionPayload {
  action: SessionBulkActionValue;
}

export interface BulkSessionActionRes {
  affected: number;
  footer: SessionFooterCounts;
}

export interface MatchBusinessCardPayload {
  fullName?: string | null;
  email?: string | null;
  jobTitle?: string | null;
  company?: string | null;
  values?: ContactFieldValueInput[];
}

export interface BusinessCardMatchRes {
  bucket: ImportRowBucketValue;
  autoMergeContactId: string | null;
  candidates: RankedCandidate[];
}

export interface MergeBusinessCardPayload extends MatchBusinessCardPayload {
  contactId: string;
  organizationId?: string | null;
}

export interface MergeBusinessCardRes {
  success: boolean;
}

// ==================== Provider connections (OAuth, GET/POST /contact-import/providers) ====================

/** Only `Google` / `Microsoft365` are OAuth-connectable providers. */
export const ProviderConnectionStatus = {
  Connected: 'Connected',
  NeedsReauth: 'NeedsReauth',
  NotConnected: 'NotConnected',
} as const;

export type ProviderConnectionStatusValue =
  (typeof ProviderConnectionStatus)[keyof typeof ProviderConnectionStatus];

export interface ProviderConnectionInfo {
  provider: ContactProviderValue;
  status: ProviderConnectionStatusValue;
  accountEmail: string | null;
}

export interface ConnectProviderRes {
  /** Null when `alreadyConnected` — skip the redirect and go straight to session creation. */
  authorizationUrl: string | null;
  alreadyConnected: boolean;
  accountEmail: string | null;
}

export interface CompleteOAuthPayload {
  code: string;
  state: string;
}
